import { supabase } from "../supabaseClient";
import { MatchState, Delivery, Player } from "../matchTypes";
import {
  saveMatchPlayerPerformance,
  recalculateSeasonStats,
  recalculateCareerStats,
  type LeagueMatchPlayer,
} from "./leagueSeasons";

interface MatchContext {
  matchId: string;
  leagueId: string;
  seasonId: string;
  fixtureId: string;
  teamAId: string;
  teamBId: string;
}

export async function updatePlayerStatsFromMatch(_match: MatchState): Promise<void> {
  // Stub preserved for non-league matches - no-op
}

export async function processLeagueMatchCompletion(
  matchState: MatchState,
  context: MatchContext
): Promise<boolean> {
  if (matchState.status !== "completed") return false;

  const { matchId, leagueId, seasonId, fixtureId, teamAId, teamBId } = context;

  // Extract performance from both innings
  const playerPerformances = extractPlayerPerformances(matchState, context);

  // Save individual match performance records
  const saved = await saveMatchPlayerPerformance(playerPerformances);
  if (!saved) return false;

  // Update fixture with result
  await updateFixtureResult(matchState, fixtureId, teamAId, teamBId);

  // Update league standings (NRR-based)
  await updateStandingsFromMatch(matchState, leagueId, seasonId, teamAId, teamBId);

  // Recalculate season stats for all players in this match
  await recalculateSeasonStats(leagueId, seasonId);

  // Recalculate career stats for affected players
  const playerIds = playerPerformances.map(p => p.player_id);
  await recalculateCareerStats(leagueId, playerIds);

  return true;
}

export async function removeLeagueMatchStats(
  matchId: string,
  leagueId: string,
  seasonId: string
): Promise<boolean> {
  // Get affected player IDs before deletion
  const { data: matchPlayers } = await supabase
    .from("league_match_players")
    .select("player_id")
    .eq("match_id", matchId);

  const playerIds = (matchPlayers || []).map(mp => mp.player_id);

  // Delete match player records
  await supabase
    .from("league_match_players")
    .delete()
    .eq("match_id", matchId);

  // Recalculate season stats
  await recalculateSeasonStats(leagueId, seasonId);

  // Recalculate career stats for affected players
  if (playerIds.length > 0) {
    await recalculateCareerStats(leagueId, playerIds);
  }

  // Recalculate standings
  await recalculateStandingsFromAllMatches(leagueId, seasonId);

  return true;
}

function extractPlayerPerformances(
  matchState: MatchState,
  context: MatchContext
): Omit<LeagueMatchPlayer, "id">[] {
  const { matchId, leagueId, seasonId, teamAId, teamBId } = context;
  const performances: Map<string, Omit<LeagueMatchPlayer, "id">> = new Map();

  const getOrCreate = (player: Player, teamId: string): Omit<LeagueMatchPlayer, "id"> => {
    const key = player.id;
    if (!performances.has(key)) {
      performances.set(key, {
        match_id: matchId,
        league_id: leagueId,
        season_id: seasonId,
        team_id: teamId,
        player_id: player.id,
        player_name: player.name,
        bat_runs: 0,
        bat_balls: 0,
        bat_fours: 0,
        bat_sixes: 0,
        bat_not_out: false,
        bat_dismissal: null,
        bat_position: null,
        bowl_overs: 0,
        bowl_balls: 0,
        bowl_runs: 0,
        bowl_wickets: 0,
        bowl_maidens: 0,
        field_catches: 0,
        field_run_outs: 0,
        field_stumpings: 0,
        is_player_of_match: false,
      });
    }
    return performances.get(key)!;
  };

  // Process each innings
  for (const innings of matchState.innings) {
    const battingTeamId = innings.battingTeam === "A" ? teamAId : teamBId;
    const bowlingTeamId = innings.bowlingTeam === "A" ? teamAId : teamBId;
    const batters = innings.battingTeam === "A" ? matchState.teamAPlayers : matchState.teamBPlayers;
    const bowlers = innings.bowlingTeam === "A" ? matchState.teamAPlayers : matchState.teamBPlayers;

    // Ensure all batters who participated are registered
    for (const batter of batters) {
      getOrCreate(batter, battingTeamId);
    }
    for (const bowler of bowlers) {
      getOrCreate(bowler, bowlingTeamId);
    }

    // Process deliveries for batting stats
    const bowlerBalls = new Map<string, { balls: number; runs: number; wickets: number; overBalls: number[]; overExtras: number[]; }>();

    for (const delivery of innings.deliveries) {
      // Batting: only count stats for legal deliveries on batter's record
      if (delivery.batterId) {
        const batter = batters.find(b => b.id === delivery.batterId) ||
                       bowlers.find(b => b.id === delivery.batterId);
        if (batter) {
          const perf = getOrCreate(batter, battingTeamId);
          if (delivery.isLegal && delivery.extraType !== "wide") {
            perf.bat_balls++;
          }
          // Runs scored by batter (exclude byes and leg byes)
          if (!delivery.extraType || delivery.extraType === "noball") {
            perf.bat_runs += delivery.runs;
            if (delivery.runs === 4 && !delivery.extraType) perf.bat_fours++;
            if (delivery.runs === 6 && !delivery.extraType) perf.bat_sixes++;
          }
          if (delivery.isWicket && delivery.batterId === delivery.batterId) {
            perf.bat_dismissal = delivery.dismissalType || delivery.dismissal || "Out";
          }
        }
      }

      // Bowling stats
      if (delivery.bowlerId) {
        if (!bowlerBalls.has(delivery.bowlerId)) {
          bowlerBalls.set(delivery.bowlerId, { balls: 0, runs: 0, wickets: 0, overBalls: [], overExtras: [] });
        }
        const bStats = bowlerBalls.get(delivery.bowlerId)!;
        if (delivery.isLegal) {
          bStats.balls++;
          bStats.overBalls.push(delivery.runs);
        } else if (delivery.extraType === "wide" || delivery.extraType === "noball") {
          bStats.overExtras.push(delivery.runs);
        }
        // Runs conceded (wides and no-balls count against bowler)
        if (delivery.extraType === "wide" || delivery.extraType === "noball") {
          bStats.runs += delivery.runs;
        } else if (delivery.extraType === "bye" || delivery.extraType === "legbye") {
          // Byes/leg-byes don't count against bowler
        } else {
          bStats.runs += delivery.runs;
        }
        if (delivery.isWicket && delivery.dismissalType !== "Run Out") {
          bStats.wickets++;
        }
      }

      // Fielding
      if (delivery.isWicket && delivery.fielderId) {
        const fielder = [...batters, ...bowlers].find(p => p.id === delivery.fielderId);
        if (fielder) {
          const fPerf = getOrCreate(fielder, bowlingTeamId);
          if (delivery.dismissalType === "Caught") fPerf.field_catches++;
          else if (delivery.dismissalType === "Run Out") fPerf.field_run_outs++;
          else if (delivery.dismissalType === "Stumped") fPerf.field_stumpings++;
        }
      }
    }

    // Apply bowler stats
    for (const [bowlerId, bStats] of bowlerBalls) {
      const bowler = bowlers.find(b => b.id === bowlerId);
      if (bowler) {
        const perf = getOrCreate(bowler, bowlingTeamId);
        perf.bowl_balls += bStats.balls;
        perf.bowl_runs += bStats.runs;
        perf.bowl_wickets += bStats.wickets;
        perf.bowl_overs = Math.floor(perf.bowl_balls / 6) + (perf.bowl_balls % 6) / 10;

        // Calculate maidens: full overs (6 legal balls) where 0 runs were conceded
        // Wides/no-balls in the over disqualify it from being a maiden
        let currentOverBalls: number[] = [];
        let currentOverExtras: number[] = [];
        let maidens = 0;
        let extraIdx = 0;
        for (let bi = 0; bi < bStats.overBalls.length; bi++) {
          currentOverBalls.push(bStats.overBalls[bi]);
          if (currentOverBalls.length === 6) {
            const extraRunsInOver = bStats.overExtras
              .slice(extraIdx, bStats.overExtras.length)
              .reduce((sum, r) => sum + r, 0);
            if (currentOverBalls.every(b => b === 0) && extraRunsInOver === 0) maidens++;
            currentOverBalls = [];
            extraIdx = bStats.overExtras.length;
          }
        }
        perf.bowl_maidens += maidens;
      }
    }

    // Set not-out status for batters
    for (const batter of batters) {
      const perf = performances.get(batter.id);
      if (perf && !batter.isOut && (perf.bat_runs > 0 || perf.bat_balls > 0)) {
        perf.bat_not_out = true;
      }
    }
  }

  return Array.from(performances.values());
}

async function updateFixtureResult(
  matchState: MatchState,
  fixtureId: string,
  teamAId: string,
  teamBId: string
): Promise<void> {
  // Determine winner
  let winnerTeamId: string | null = null;
  let marginRuns: number | null = null;
  let marginWickets: number | null = null;
  let resultText = "";

  if (matchState.innings.length >= 2) {
    const inn1 = matchState.innings[0];
    const inn2 = matchState.innings[1];
    const team1Score = inn1.totalRuns;
    const team2Score = inn2.totalRuns;

    if (team1Score > team2Score) {
      winnerTeamId = inn1.battingTeam === "A" ? teamAId : teamBId;
      marginRuns = team1Score - team2Score;
      resultText = `${inn1.battingTeam === "A" ? matchState.teamAName : matchState.teamBName} won by ${marginRuns} runs`;
    } else if (team2Score > team1Score) {
      winnerTeamId = inn2.battingTeam === "A" ? teamAId : teamBId;
      const wicketsRemaining = (matchState.format === "INDOOR" ? 8 : 10) - inn2.wickets;
      marginWickets = wicketsRemaining;
      resultText = `${inn2.battingTeam === "A" ? matchState.teamAName : matchState.teamBName} won by ${marginWickets} wickets`;
    } else {
      resultText = "Match Tied";
    }
  }

  await supabase
    .from("league_fixtures")
    .update({
      status: "COMPLETED",
      result_text: resultText,
      winner_team_id: winnerTeamId,
      margin_runs: marginRuns,
      margin_wickets: marginWickets,
      match_id: fixtureId ? undefined : undefined,
      updated_at: new Date().toISOString(),
    })
    .eq("id", fixtureId);
}

async function updateStandingsFromMatch(
  matchState: MatchState,
  leagueId: string,
  seasonId: string,
  teamAId: string,
  teamBId: string
): Promise<void> {
  await recalculateStandingsFromAllMatches(leagueId, seasonId);
}

async function recalculateStandingsFromAllMatches(
  leagueId: string,
  seasonId: string
): Promise<void> {
  // Fetch all completed fixtures for this league+season
  const { data: fixtures, error } = await supabase
    .from("league_fixtures")
    .select("*")
    .eq("league_id", leagueId)
    .eq("status", "COMPLETED");

  if (error || !fixtures) return;

  // Get league points config
  const { data: league } = await supabase
    .from("leagues")
    .select("points_win, points_tie, points_no_result")
    .eq("id", leagueId)
    .maybeSingle();

  const pointsWin = league?.points_win ?? 2;
  const pointsTie = league?.points_tie ?? 1;
  const pointsNoResult = league?.points_no_result ?? 1;

  // Get all teams
  const { data: teams } = await supabase
    .from("league_teams")
    .select("id, team_name")
    .eq("league_id", leagueId);

  if (!teams) return;

  // Build standings from fixtures + match data
  const standings = new Map<string, {
    team_id: string;
    team_name: string;
    matches_played: number;
    wins: number;
    losses: number;
    ties: number;
    no_results: number;
    points: number;
    runs_for: number;
    runs_against: number;
    overs_faced: number;
    overs_bowled: number;
  }>();

  for (const team of teams) {
    standings.set(team.id, {
      team_id: team.id,
      team_name: team.team_name,
      matches_played: 0,
      wins: 0,
      losses: 0,
      ties: 0,
      no_results: 0,
      points: 0,
      runs_for: 0,
      runs_against: 0,
      overs_faced: 0,
      overs_bowled: 0,
    });
  }

  // Process each completed fixture
  for (const fix of fixtures) {
    const homeId = fix.home_team_id || fix.team_a_id;
    const awayId = fix.away_team_id || fix.team_b_id;
    if (!homeId || !awayId) continue;

    const homeStanding = standings.get(homeId);
    const awayStanding = standings.get(awayId);
    if (!homeStanding || !awayStanding) continue;

    homeStanding.matches_played++;
    awayStanding.matches_played++;

    // Get match data for NRR calculation
    if (fix.match_id) {
      const { data: matchRow } = await supabase
        .from("matches")
        .select("match_data")
        .eq("id", fix.match_id)
        .maybeSingle();

      if (matchRow?.match_data && typeof matchRow.match_data === "object") {
        const md = matchRow.match_data as MatchState;
        if (md.innings && md.innings.length >= 2) {
          const inn1 = md.innings[0];
          const inn2 = md.innings[1];

          // Determine which team batted first
          const firstBatTeamId = inn1.battingTeam === "A" ? homeId : awayId;
          const secondBatTeamId = inn2.battingTeam === "A" ? homeId : awayId;

          // Runs for/against and overs
          const firstOvers = inn1.totalBalls / 6;
          const secondOvers = inn2.totalBalls / 6;

          const firstStanding = standings.get(firstBatTeamId);
          const secondStanding = standings.get(secondBatTeamId);
          if (firstStanding && secondStanding) {
            firstStanding.runs_for += inn1.totalRuns;
            firstStanding.overs_faced += firstOvers;
            firstStanding.runs_against += inn2.totalRuns;
            firstStanding.overs_bowled += secondOvers;

            secondStanding.runs_for += inn2.totalRuns;
            secondStanding.overs_faced += secondOvers;
            secondStanding.runs_against += inn1.totalRuns;
            secondStanding.overs_bowled += firstOvers;
          }
        }
      }
    }

    // Points
    if (fix.winner_team_id === homeId) {
      homeStanding.wins++;
      homeStanding.points += pointsWin;
      awayStanding.losses++;
    } else if (fix.winner_team_id === awayId) {
      awayStanding.wins++;
      awayStanding.points += pointsWin;
      homeStanding.losses++;
    } else if (fix.result_text?.toLowerCase().includes("tied") || fix.result_text?.toLowerCase().includes("tie")) {
      homeStanding.ties++;
      awayStanding.ties++;
      homeStanding.points += pointsTie;
      awayStanding.points += pointsTie;
    } else {
      homeStanding.no_results++;
      awayStanding.no_results++;
      homeStanding.points += pointsNoResult;
      awayStanding.points += pointsNoResult;
    }
  }

  // Upsert standings
  for (const [teamId, s] of standings) {
    const nrr = calculateNRR(s.runs_for, s.overs_faced, s.runs_against, s.overs_bowled);

    await supabase
      .from("league_standings")
      .upsert({
        league_id: leagueId,
        team_id: teamId,
        team_name: s.team_name,
        matches_played: s.matches_played,
        wins: s.wins,
        losses: s.losses,
        ties: s.ties,
        no_results: s.no_results,
        points: s.points,
        runs_for: s.runs_for,
        runs_against: s.runs_against,
        overs_faced: s.overs_faced,
        overs_bowled: s.overs_bowled,
        net_run_rate: nrr,
        season_id: seasonId || null,
        updated_at: new Date().toISOString(),
      }, { onConflict: "league_id,team_id" });
  }
}

function calculateNRR(runsFor: number, oversFaced: number, runsAgainst: number, oversBowled: number): number {
  if (oversFaced === 0 || oversBowled === 0) return 0;
  return (runsFor / oversFaced) - (runsAgainst / oversBowled);
}
